import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { validatePin, sendPin, setSessionToken } from '../Services/services';
import { useBranding } from '../Context/BrandingContext';
import { EMAIL_SUBSYSTEM_USER_MESSAGE, isEmailSubsystemNotice } from '../constants/authMessages';

function Verify({ onVerifyComplete, onBack, onCancel, variant = 'page', initialError }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError || '');
  const [success, setSuccess] = useState('');
  const [resending, setResending] = useState(false);
  const { email } = useSelector((state) => state.auth);
  const { branding } = useBranding();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await validatePin(email, code);
      const data = response.data;
      
      if (data.success) {
        // Save session token to localStorage for persistence across page reloads
        if (data.sessionToken) {
          setSessionToken(data.sessionToken);
        }
        
        // Call parent handler with auth data
        if (onVerifyComplete) {
          onVerifyComplete({
            email: email,
            role: data.role || data.engagementStatus || 'guest',
            isSupportRep: data.isSupportRep || false,
            fullName: data.fullName,
            company: data.company,
            phone: data.phone,
            jobTitle: data.jobTitle,
            partSales: data.partSales,
            mro: data.mro,
            roles: data.roles,
          });
        }
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Invalid verification code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    setSuccess('');
    try {
      const response = await sendPin(email);
      if (response.data?.success) {
        setSuccess('Verification code sent! Please check your email.');
      } else {
        setError(EMAIL_SUBSYSTEM_USER_MESSAGE);
      }
    } catch (err) {
      setError(EMAIL_SUBSYSTEM_USER_MESSAGE);
    } finally {
      setResending(false);
    }
  };

  const content = (
    <>
      <div style={styles.modalHeader}>
        <h1 style={styles.modalTitle}>Verify Your Email</h1>
        <p style={styles.modalHelp}>Enter the pin code that was just emailed to you.</p>
        <p style={styles.modalSubtitle}>We sent a verification code to {email}</p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label htmlFor="pin" style={styles.modalLabel}>PIN Code</label>
          <input
            id="pin"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter your PIN code"
            required
            maxLength={10}
            style={styles.modalInput}
            disabled={loading}
            autoFocus
          />
        </div>

        {error && (
          <div style={isEmailSubsystemNotice(error) ? styles.modalInfo : styles.modalError}>{error}</div>
        )}
        {success && <div style={styles.modalSuccess}>{success}</div>}

        <button type="submit" style={{ ...styles.modalPrimaryButton, backgroundColor: branding.primaryColor }} disabled={loading || code.length < 4}>
          {loading ? 'Verifying...' : 'Verify & Continue'}
        </button>

        <button type="button" onClick={onCancel || onBack} style={styles.modalSecondaryButton} disabled={loading}>
          Cancel
        </button>

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleResendCode}
            style={styles.modalLinkButton}
            disabled={loading || resending}
          >
            {resending ? 'Resending...' : "Didn't receive a code? Resend"}
          </button>
        </div>
      </form>
    </>
  );

  if (variant === 'modal') {
    return <div>{content}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ ...styles.header, borderBottom: `2px solid ${branding.primaryColor}` }}>
          <p style={{ ...styles.companyName, color: branding.primaryColor }}>{branding.companyName}</p>
          <h1 style={{ ...styles.title, color: branding.primaryColor }}>Customer Portal</h1>
          <p style={styles.subtitle}>Verify your identity</p>
        </div>
        <p style={styles.instruction}>
          Enter the verification code sent to <strong>{email}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter code"
            required
            maxLength={10}
            style={styles.input}
            disabled={loading}
          />

          {error && (
            <div style={isEmailSubsystemNotice(error) ? styles.infoNotice : styles.error}>{error}</div>
          )}
          {success && <div style={styles.success}>{success}</div>}

          <button type="submit" style={{ ...styles.button, backgroundColor: branding.primaryColor }} disabled={loading || code.length < 4}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>

          <button
            type="button"
            onClick={handleResendCode}
            style={{ ...styles.resendButton, color: branding.primaryColor }}
            disabled={loading || resending}
          >
            {resending ? 'Resending...' : 'Resend Code'}
          </button>

          <button
            type="button"
            onClick={onBack}
            style={{ ...styles.backButton, color: branding.primaryColor, border: `1px solid ${branding.primaryColor}` }}
            disabled={loading}
          >
            Back
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '2px solid #0066cc',
  },
  companyName: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700',
    color: '#0066cc',
  },
  subtitle: {
    margin: '0',
    color: '#666',
    fontSize: '16px',
  },
  instruction: {
    margin: '0 0 16px 0',
    color: '#333',
    fontSize: '14px',
    textAlign: 'center',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '24px',
    textAlign: 'center',
    letterSpacing: '8px',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '8px',
  },
  resendButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#0066cc',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginBottom: '8px',
    textDecoration: 'underline',
  },
  backButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#0066cc',
    border: '1px solid #0066cc',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    fontSize: '14px',
    marginBottom: '16px',
  },
  infoNotice: {
    color: '#1e40af',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '10px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '4px',
    border: '1px solid #bfdbfe',
    lineHeight: 1.45,
  },
  success: {
    color: '#28a745',
    fontSize: '14px',
    marginBottom: '16px',
  },

  // Modal variant (Figma Make-inspired)
  modalHeader: {
    marginBottom: 20,
  },
  modalTitle: {
    margin: '0 0 8px 0',
    fontSize: 24,
    fontWeight: 600,
    color: 'var(--foreground)',
  },
  modalHelp: {
    margin: '0 0 6px 0',
    fontSize: 14,
    color: 'var(--foreground)',
  },
  modalSubtitle: {
    margin: 0,
    fontSize: 14,
    color: 'var(--muted-foreground)',
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--foreground)',
  },
  modalInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--input-background)',
    color: 'var(--foreground)',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  modalPrimaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: 'none',
    color: 'var(--primary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalSecondaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalLinkButton: {
    border: 'none',
    background: 'transparent',
    color: 'var(--muted-foreground)',
    cursor: 'pointer',
    fontSize: 13,
    textDecoration: 'none',
  },
  modalError: {
    color: 'var(--destructive)',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
  },
  modalInfo: {
    color: '#1e3a8a',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.35)',
    lineHeight: 1.45,
  },
  modalSuccess: {
    color: '#16a34a',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
    border: '1px solid rgba(34, 197, 94, 0.25)',
  },
};

export default Verify;

